/**
 * types.ts — Types publics du sous-module catalog/extended (D3-recentré R3-bis).
 *
 * 4 entités catalog ajoutées en migration 20260525182000_catalog_extended_entities.sql :
 *   - product_variants       (variantes d'un produit)
 *   - product_groups         (groupes nominaux)
 *   - product_group_members  (jonction many-to-many products ↔ groups)
 *   - menus                  (menus restaurant)
 *   - recipes                (recettes hiérarchiques)
 *
 * Tous types dérivés STRICTEMENT de Database['public']['Tables'].
 *
 * Invariants :
 *   4.A.10 — tenant_id jamais dans les inputs ; RLS côté serveur.
 *   4.B.4  — 0 any.
 */

import { z } from 'zod';
import type { Database } from '@/integrations/supabase/types';

// ---------------------------------------------------------------------------
// Row / Insert / Update types
// ---------------------------------------------------------------------------

export type ProductVariantRow = Database['public']['Tables']['product_variants']['Row'];
export type ProductVariantInsert = Database['public']['Tables']['product_variants']['Insert'];
export type ProductVariantUpdate = Database['public']['Tables']['product_variants']['Update'];

export type ProductGroupRow = Database['public']['Tables']['product_groups']['Row'];
export type ProductGroupInsert = Database['public']['Tables']['product_groups']['Insert'];
export type ProductGroupUpdate = Database['public']['Tables']['product_groups']['Update'];

export type ProductGroupMemberRow = Database['public']['Tables']['product_group_members']['Row'];
export type ProductGroupMemberInsert =
  Database['public']['Tables']['product_group_members']['Insert'];

export type MenuRow = Database['public']['Tables']['menus']['Row'];
export type MenuInsert = Database['public']['Tables']['menus']['Insert'];
export type MenuUpdate = Database['public']['Tables']['menus']['Update'];

export type RecipeRow = Database['public']['Tables']['recipes']['Row'];
export type RecipeInsert = Database['public']['Tables']['recipes']['Insert'];
export type RecipeUpdate = Database['public']['Tables']['recipes']['Update'];

// ---------------------------------------------------------------------------
// Zod schemas — product_variants
// ---------------------------------------------------------------------------

export const createProductVariantSchema = z.object({
  product_id: z.string().uuid(),
  name: z.string().min(1).max(255),
  sku: z.string().max(64).nullable().optional(),
  price_cents: z.number().int().nonnegative().nullable().optional(),
  stock_quantity: z.number().int().nonnegative().default(0),
  is_active: z.boolean().default(true),
  attributes: z.record(z.string(), z.unknown()).default({}),
});
export type CreateProductVariantInput = z.infer<typeof createProductVariantSchema>;

export const updateProductVariantSchema = createProductVariantSchema.partial();
export type UpdateProductVariantInput = z.infer<typeof updateProductVariantSchema>;

// ---------------------------------------------------------------------------
// Zod schemas — product_groups
// ---------------------------------------------------------------------------

export const createProductGroupSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1024).nullable().optional(),
  display_order: z.number().int().nonnegative().default(0),
});
export type CreateProductGroupInput = z.infer<typeof createProductGroupSchema>;

export const updateProductGroupSchema = createProductGroupSchema.partial();
export type UpdateProductGroupInput = z.infer<typeof updateProductGroupSchema>;

// ---------------------------------------------------------------------------
// Zod schemas — product_group_members (jonction)
// ---------------------------------------------------------------------------

export const createProductGroupMemberSchema = z.object({
  group_id: z.string().uuid(),
  product_id: z.string().uuid(),
});
export type CreateProductGroupMemberInput = z.infer<typeof createProductGroupMemberSchema>;

// ---------------------------------------------------------------------------
// Zod schemas — menus
// ---------------------------------------------------------------------------

export const createMenuSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1024).nullable().optional(),
  items: z.array(z.unknown()).default([]),
  active_period: z.record(z.string(), z.unknown()).nullable().optional(),
  price_cents: z.number().int().nonnegative().nullable().optional(),
  is_active: z.boolean().default(true),
  display_order: z.number().int().nonnegative().default(0),
});
export type CreateMenuInput = z.infer<typeof createMenuSchema>;

export const updateMenuSchema = createMenuSchema.partial();
export type UpdateMenuInput = z.infer<typeof updateMenuSchema>;

// ---------------------------------------------------------------------------
// Zod schemas — recipes (hiérarchiques)
// ---------------------------------------------------------------------------

export const createRecipeSchema = z.object({
  product_id: z.string().uuid().nullable().optional(),
  parent_id: z.string().uuid().nullable().optional(),
  name: z.string().min(1).max(255),
  ingredients: z.array(z.unknown()).default([]),
  yield_quantity: z.number().nonnegative().nullable().optional(),
  yield_unit: z.string().max(32).nullable().optional(),
  notes: z.string().max(2048).nullable().optional(),
});
export type CreateRecipeInput = z.infer<typeof createRecipeSchema>;

export const updateRecipeSchema = createRecipeSchema.partial();
export type UpdateRecipeInput = z.infer<typeof updateRecipeSchema>;

// ---------------------------------------------------------------------------
// Filtres list
// ---------------------------------------------------------------------------

export type ListProductVariantsFilters = {
  readonly productId?: string;
  readonly includeInactive?: boolean;
};

export type ListProductGroupsFilters = {
  readonly nameContains?: string;
};

export type ListProductGroupMembersFilters = {
  readonly groupId?: string;
  readonly productId?: string;
};

export type ListMenusFilters = {
  readonly includeInactive?: boolean;
};

export type ListRecipesFilters = {
  readonly productId?: string;
  readonly parentId?: string | null;
  readonly rootOnly?: boolean;
};

// ---------------------------------------------------------------------------
// Aliases lisibles
// ---------------------------------------------------------------------------

export type ProductVariant = ProductVariantRow;
export type ProductGroup = ProductGroupRow;
export type ProductGroupMember = ProductGroupMemberRow;
export type Menu = MenuRow;
export type Recipe = RecipeRow;
